import type { UsePermissionQueryModel } from "@tellescope/tanstack-query";
import { CONTACT_DETAILS_BLOCKS_FLAT_MAP } from "../constants";
import type { BlockBaseConfig, ContactDetailsBlockType, ContactDetailsFeature } from "../types";

export interface IsBlockAvailableOptions {
  /**
   * Whether the viewer can read each model, keyed by model name. A model absent from the map counts
   * as unreadable.
   */
  readableModels: Partial<Record<UsePermissionQueryModel, boolean>>;
  /**
   * Org-feature gate injected by the app. When omitted, the feature half of the check passes — a
   * caller that doesn't supply it must not lose every feature-gated block.
   */
  isFeatureEnabled?: (feature: ContactDetailsFeature) => boolean;
}

/**
 * Whether a block should be offered and rendered for the current viewer: every model the block reads
 * must be readable, and the org feature it requires (if any) must be enabled.
 *
 * The single source of truth for block gating — the add-block dialog and the view body both go
 * through it, so they cannot disagree about which blocks exist. Pure and hook-free so it can be
 * unit-tested in isolation; `useBlockAvailability` supplies the permission inputs.
 */
export const isBlockAvailable = (
  blockType: ContactDetailsBlockType,
  { readableModels, isFeatureEnabled }: IsBlockAvailableOptions
): boolean => {
  const config: BlockBaseConfig = CONTACT_DETAILS_BLOCKS_FLAT_MAP[blockType];
  const { requiredReadModels = [], requiredFeature } = config;

  if (!requiredReadModels.every((model) => readableModels[model])) return false;
  if (requiredFeature && isFeatureEnabled && !isFeatureEnabled(requiredFeature)) return false;

  return true;
};
